import React, {useContext, useEffect, useState} from "react";
import TrackVASContext from "../../TrackVASContext";
import {PageTitle} from "../../../_metronic/layout/core";
import {genericGetAll, genericDelete} from "../generic/crud";
import {ENTITY_ID_JOB_CARD} from "../generic/Constants";
import {showConfirmDialog} from "../../generic-components/ConfirmDialog";
import {showToast, TOAST_SUCCESS} from "../../utils/toast/Toast";
import {JobCardAddEditDialog} from "./JobCardAddEditDialog";
import {JobCardDetailsDialog} from "./JobCardDetailsDialog";
import {WaterWashWizardDialog} from "../waterWash/WaterWashWizardDialog";
import {useLocation, useParams, useNavigate} from "react-router-dom";
import {Pagination} from "react-bootstrap";
import {JobCardItem} from "./JobCardItem";

export function JobCardListPage() {
    const myData = useContext(TrackVASContext);
    const navigate = useNavigate();
    const { branchId } = useParams();
    const location = useLocation();
    const [branchInfo, setBranchInfo] = useState(location.state || {});
    
    // State
    const [activeJobCards, setActiveJobCards] = useState([]);
    const [closedJobCards, setClosedJobCards] = useState([]);
    const [closedTotal, setClosedTotal] = useState(0);
    const [closedPage, setClosedPage] = useState(1);
    const [closedSize] = useState(10); // Page size for closed cards
    
    const [loadingActive, setLoadingActive] = useState(true);
    const [loadingClosed, setLoadingClosed] = useState(true);
    
    const [showModal, setShowModal] = useState(false);
    const [showModelId, setShowModelId] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [detailsJobCardId, setDetailsJobCardId] = useState(null);
    const [showWaterWashWizard, setShowWaterWashWizard] = useState(false);
    const [reloadCounter, setReloadCounter] = useState(0);

    const profileBreadCrumbs = [
        { title: "Home", path: "/dashboard", isSeparator: false, isActive: false },
        { title: "Job Cards", path: "/jobCards", isSeparator: false, isActive: false },
        { title: "", path: "", isSeparator: true, isActive: false },
    ];

    useEffect(() => {
        myData.setBaseData({
            toolBarButtons: [
                {
                    name: "Create Job Card",
                    color: "primary",
                    icon: "fa fa-plus",
                    onClick: () => { setShowModelId(null); setShowModal(true); },
                },
            ],
        });

        return () => {
            myData.setBaseData({
                toolBarButtons: []
            });
        };
    }, []);

    // Fetch Data
    useEffect(() => {
        fetchActiveCards();
        fetchClosedCards();
    }, [branchId, reloadCounter, closedPage]);

    const fetchActiveCards = async () => {
        setLoadingActive(true);
        try {
            // Fetch OPEN and IN_PROGRESS separately to ensure we get all
            const openReq = genericGetAll(ENTITY_ID_JOB_CARD, {
                page: 1, sizePerPage: 100, currentIndex: 0,
                fixedFilterColumn: 'branchId', fixedFilterData: branchId,
                searchField: 'status', searchString: 'OPEN',
                sortBy: 'committedDate', sortOrder: 'asc' // Oldest open first?
            });

            const progressReq = genericGetAll(ENTITY_ID_JOB_CARD, {
                page: 1, sizePerPage: 100, currentIndex: 0,
                fixedFilterColumn: 'branchId', fixedFilterData: branchId,
                searchField: 'status', searchString: 'IN_PROGRESS',
                sortBy: 'committedDate', sortOrder: 'asc'
            });

            const [openRes, progressRes] = await Promise.all([openReq, progressReq]);

            const openCards = openRes.data.entities || [];
            const progressCards = progressRes.data.entities || [];

            // Merge and sort (optional, but good to keep consistent)
            setActiveJobCards([...openCards, ...progressCards]);
        } catch (error) {
            console.error("Error fetching active cards", error);
        } finally {
            setLoadingActive(false);
        }
    };

    const fetchClosedCards = async () => {
        setLoadingClosed(true);
        try {
            const res = await genericGetAll(ENTITY_ID_JOB_CARD, {
                page: closedPage, sizePerPage: closedSize,
                currentIndex: (closedPage - 1) * closedSize,
                fixedFilterColumn: 'branchId', fixedFilterData: branchId,
                searchField: 'status', searchString: 'CLOSED',
                sortBy: 'committedDate', sortOrder: 'desc' // Latest closed first
            });
            setClosedJobCards(res.data.entities || []);
            setClosedTotal(res.data.totalCount || 0);
        } catch (error) {
            console.error("Error fetching closed cards", error);
        } finally {
            setLoadingClosed(false);
        }
    };

    const reloadData = () => {
        setReloadCounter(prev => prev + 1);
    };

    const handleDelete = async (id) => {
        const result = await showConfirmDialog("Do you really want to delete this Job Card?");
        if (result) {
            genericDelete(ENTITY_ID_JOB_CARD, id)
                .then(() => {
                    showToast("Job Card Deleted!", TOAST_SUCCESS);
                    reloadData();
                })
                .catch(() => {});
        }
    };
    
    // Pagination Component
    const renderPagination = () => {
        if (closedTotal <= closedSize) return null;
        
        const totalPages = Math.ceil(closedTotal / closedSize);
        let items = [];
        for (let number = 1; number <= totalPages; number++) {
             items.push(
                <Pagination.Item key={number} active={number === closedPage} onClick={() => setClosedPage(number)}>
                  {number}
                </Pagination.Item>,
              );
        }

        return (
             <div className="d-flex justify-content-center mt-4">
                <Pagination>{items}</Pagination>
             </div>
        );
    };

    return (
        <>
            <PageTitle breadcrumbs={profileBreadCrumbs}>
                {branchInfo.branchName ? `Job Cards - ${branchInfo.branchName}` : 'Job Cards'}
            </PageTitle>

            {/* Quick Action Buttons - Mobile Friendly */}
            <div className="row g-4 mb-8">
                <div className="col-md-6">
                    <div
                        className="card card-flush h-100 cursor-pointer hover-elevate-up bg-light-primary"
                        onClick={() => { setShowModelId(null); setShowModal(true); }}
                        style={{
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            border: '2px solid #009ef7'
                        }}
                    >
                        <div className="card-body d-flex flex-column justify-content-center align-items-center text-center py-8">
                            <i className="bi bi-plus-circle fs-3x text-primary mb-4"></i>
                            <h3 className="text-primary fw-bolder fs-2 mb-2">Create Job Card</h3>
                            <p className="text-primary opacity-75 mb-0 fs-6">Add a new job card for this branch</p>
                        </div>
                    </div>
                </div>

                <div className="col-md-6">
                    <div
                        className="card card-flush h-100 cursor-pointer hover-elevate-up bg-light-success"
                        onClick={() => setShowWaterWashWizard(true)}
                        style={{
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            border: '2px solid #50cd89'
                        }}
                    >
                        <div className="card-body d-flex flex-column justify-content-center align-items-center text-center py-8">
                            <i className="bi bi-droplet-fill fs-3x text-success mb-4"></i>
                            <h3 className="text-success fw-bolder fs-2 mb-2">Create Water Wash</h3>
                            <p className="text-success opacity-75 mb-0 fs-6">Record a new water wash service</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Active Cards Section */}
            <div className="card mb-8">
                <div className="card-header border-0 pt-5">
                    <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bolder fs-3 mb-1">Active Jobs</span>
                        <span className="text-muted mt-1 fw-bold fs-7">{activeJobCards.length} Open or In-Progress</span>
                    </h3>
                </div>
                <div className="card-body pt-3 pb-5">
                    {loadingActive ? (
                        <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
                    ) : activeJobCards.length > 0 ? (
                        <div className="row">
                            {activeJobCards.map(card => <JobCardItem 
    key={card.id} 
    card={card}
    onDetailsClick={(id) => { setDetailsJobCardId(id); setShowDetailsModal(true); }}
    onEditClick={(id) => { setShowModelId(id); setShowModal(true); }}
    onDeleteClick={(id) => handleDelete(id)}
/>)}
                        </div>
                    ) : (
                         <div className="alert alert-light-primary d-flex align-items-center p-5 mb-0">
                            <i className="bi bi-info-circle-fill fs-2 text-primary me-3"></i>
                            <div className="text-primary fw-bold">No active job cards found for this branch.</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Closed Cards Section */}
            <div className="card">
                <div className="card-header border-0 pt-5">
                    <h3 className="card-title align-items-start flex-column">
                        <span className="card-label fw-bolder fs-3 mb-1">History</span>
                        <span className="text-muted mt-1 fw-bold fs-7">Closed Job Cards</span>
                    </h3>
                </div>
                <div className="card-body pt-3 pb-5">
                     {loadingClosed ? (
                        <div className="text-center py-5"><span className="spinner-border text-primary"></span></div>
                    ) : closedJobCards.length > 0 ? (
                        <>
                            <div className="row">
                                {closedJobCards.map(card => <JobCardItem 
    key={card.id} 
    card={card}
    onDetailsClick={(id) => { setDetailsJobCardId(id); setShowDetailsModal(true); }}
    onEditClick={(id) => { setShowModelId(id); setShowModal(true); }}
    onDeleteClick={(id) => handleDelete(id)}
/>)}
                            </div>
                            {renderPagination()}
                        </>
                    ) : (
                         <div className="text-center text-muted fw-bold py-5">
                            No closed job cards history available.
                        </div>
                    )}
                </div>
            </div>

            <JobCardAddEditDialog
                show={showModal}
                handleClose={() => setShowModal(false)}
                id={showModelId}
                onSave={reloadData}
                preSelectedBranch={branchId ? {id: branchId, name: branchInfo.branchName || 'Selected Branch'} : null}
            />

            <JobCardDetailsDialog
                show={showDetailsModal}
                handleClose={() => setShowDetailsModal(false)}
                jobCardId={detailsJobCardId}
                onStatusChange={reloadData}
            />

            <WaterWashWizardDialog
                show={showWaterWashWizard}
                handleClose={() => setShowWaterWashWizard(false)}
                branchId={branchId}
                branchName={branchInfo.branchName || 'Selected Branch'}
            />
        </>
    );
}